import type { CommunityRole } from './communityPermissions';

export const COMMUNITY_SUPPORT_TEAM_LABEL = 'Support Team';

/** App client IDs are C#####; admin portal actors use Mongo User ObjectIds. */
export function isCommunityStaffActorId(actorId: string | undefined): boolean {
  if (!actorId?.trim()) return false;
  const id = actorId.trim();
  if (/^C\d{5,}$/i.test(id)) return false;
  return /^[a-f0-9]{24}$/i.test(id);
}

function isStaffSender(
  senderClientId: string | undefined,
  memberRoleByClientId: Map<string, CommunityRole>,
  memberIsStaffByClientId?: Map<string, boolean>
): boolean {
  if (!senderClientId) return false;
  if (memberIsStaffByClientId?.get(senderClientId) === true) return true;
  if (isCommunityStaffActorId(senderClientId)) return true;
  const role = memberRoleByClientId.get(senderClientId);
  return role === 'owner' || role === 'moderator';
}

/** Client app: staff messages show as Support Team; clients show their real name. */
export function resolveClientAppSenderDisplayName(
  senderClientId: string | undefined,
  cachedName: string | undefined,
  memberNameByClientId: Map<string, string>,
  memberRoleByClientId: Map<string, CommunityRole>,
  memberIsStaffByClientId?: Map<string, boolean>
): string {
  if (isStaffSender(senderClientId, memberRoleByClientId, memberIsStaffByClientId)) {
    return COMMUNITY_SUPPORT_TEAM_LABEL;
  }

  if (senderClientId) {
    const fromMember = memberNameByClientId.get(senderClientId);
    if (fromMember && fromMember !== senderClientId) return fromMember;
  }

  const cached = cachedName?.trim();
  if (cached && cached !== 'Member') return cached;
  return senderClientId || cached || 'Unknown';
}

/** Admin portal: show client/staff real names from membership + cached sender. */
export function resolveAdminSenderDisplayName(
  senderClientId: string | undefined,
  cachedName: string | undefined,
  memberNameByClientId: Map<string, string>
): string {
  if (senderClientId) {
    const fromMember = memberNameByClientId.get(senderClientId);
    if (fromMember && fromMember !== senderClientId) return fromMember;
  }

  const cached = cachedName?.trim();
  if (cached && cached !== 'Member') return cached;
  return senderClientId || cached || 'Unknown';
}
